import { useEffect, useState } from 'react';
import { format, isBefore, parse, isToday, parseISO } from 'date-fns';
import { useMutation } from 'react-query';
import { Form, FormInstance } from 'antd';
import { isEmpty } from 'lodash';

import { getAppointments, getFullTechnicianAvailability, getRescheduleAppointments } from 'queries';
import { JsonDataItem } from 'types';

export const useGetAppointments = (
  form: FormInstance,
  appointmentDuration: number,
  customerId?: number,
  isRescheduling?: boolean,
  serviceId?: string,
  serviceSelection?: JsonDataItem | null,
  appointmentSuburbId?: number,
  searchId?: number,
  changedFields?: JsonDataItem,
  serviceSelectionId?: number,
  isOverrideModeEnabled?: boolean,
) => {
  const [slots, setSlots] = useState<JsonDataItem | null>();

  const { mutate: checkIsRemoteServiceAvailable, data: remoteTodaySlots } = useMutation(getAppointments);
  const { mutate: getTimeSlots, isLoading: isSlotsLoading } = useMutation(
    isOverrideModeEnabled
      ? getFullTechnicianAvailability
      : isRescheduling
        ? getRescheduleAppointments
        : getAppointments,
    {
      onSuccess: (data, variables) => {
        if (!isEmpty(data.data)) {
          setSlots((prevSlots) => {
            // Merge with already fetched slots
            const newSlots = {
              appointments: { ...prevSlots?.appointments, ...data?.data.appointments },
              technicians: { ...prevSlots?.technicians, ...data?.data.technicians },
              appointmentDatesMetaInfo: {
                ...prevSlots?.appointmentDatesMetaInfo,
                ...data?.data.appointmentDatesMetaInfo,
              },
            };

            // Convert appointments object to array of key-value pairs and sort by date
            const sortedAppointments: JsonDataItem = Object.entries(newSlots.appointments)
              .sort(([dateA], [dateB]) =>
                isBefore(parse(dateA, 'dd-MM-yyyy', new Date()), parse(dateB, 'dd-MM-yyyy', new Date())) ? -1 : 1,
              )
              .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

            // Update the data object with sorted appointments
            const sortedData: JsonDataItem = { ...newSlots, appointments: sortedAppointments };

            return sortedData; // Update slots state with the new sorted data
          });

          // check is remote service available today
          !isRescheduling &&
          isToday(parseISO(variables.startDate)) &&
          checkIsRemoteServiceAvailable({ ...variables, daysAmount: 1, serviceType: 2 });
        } else {
          setSlots(null);
        }
      },
      onError: () => {
        setSlots(null);
      },
    },
  );

  const fetchAppointments = (date: Date, days: number, clear?: boolean) => {
    clear && setSlots(null);
    const config = form.getFieldsValue();
    config.jumpToDate = format(config.jumpToDate || new Date(), 'yyyy-MM-dd');
    config.surroundingGeeks = Number(config.surroundingGeeks);
    config.nominatedTech = config.nominatedTech
      ?.split(',')
      .map((item: string) => Number(item.replace('_parent', '')))
      .filter(Boolean);
    const appointmentDate = date || new Date();

    if (appointmentDuration) {
      getTimeSlots(
        isOverrideModeEnabled
          ? {
              technicianId: config?.nominatedTech[0],
              daysAmount: days,
              startDate: format(appointmentDate, 'yyyy-MM-dd'),
              serviceSelectionId: serviceSelectionId,
              serviceType: config?.jobType,
            }
          : isRescheduling
            ? {
                startDate: format(appointmentDate, 'yyyy-MM-dd'),
                jobId: serviceId,
                serviceType: config?.jobType,
                daysAmount: days,
                techIds: config?.nominatedTech,
                surroundingGeeks: config?.surroundingGeeks,
                skillIds: config.skillsets,
                duration: appointmentDuration,
              }
            : {
                startDate: format(appointmentDate, 'yyyy-MM-dd'),
                selectionId: serviceSelection?.ID,
                daysAmount: days,
                serviceType: config?.jobType,
                suburb: appointmentSuburbId,
                techIds: config?.nominatedTech,
                customerId: customerId,
                duration: appointmentDuration,
                surroundingGeeks: config?.surroundingGeeks,
                searchId: searchId,
                skillIds: config.skillsets,
              },
      );
    }
  };

  const skillsets = Form.useWatch('skillsets', { form });
  useEffect(() => {
    const date = changedFields?.jumpToDate || new Date();
    appointmentDuration && typeof skillsets !== 'undefined' && fetchAppointments(date, 5, true);
  }, [changedFields, appointmentDuration, skillsets, appointmentSuburbId, isOverrideModeEnabled]);

  useEffect(() => {
    const date = form.getFieldValue('jumpToDate') || new Date();
    // trigger search if surrounding are was not enabled before
    if (
      !isSlotsLoading &&
      slots === null &&
      !form.getFieldValue('surroundingGeeks') &&
      typeof skillsets !== 'undefined'
    ) {
      form.setFieldValue('surroundingGeeks', true);
      fetchAppointments(date, 5, true);
    }
  }, [isSlotsLoading]);

  return { slots, remoteTodaySlots, isSlotsLoading, fetchAppointments };
};
